// Pure selection logic for the nav rail's Dashboards > Recent/Favorites
// folders — extracted from app-shell.tsx so it's unit-testable without the
// JSX shell.
import type { DashboardListItem } from '@cortex/types';

export const recentDashboardsCap = 5;
const dayMs = 24 * 60 * 60 * 1000;

/** A→Z by name — both folders display alphabetically. */
const byName = (a: DashboardListItem, b: DashboardListItem) => a.name.localeCompare(b.name);

/**
 * The Recent folder's members: dashboards that are new (created) or updated
 * (row or widget content) within the last day, excluding favorites (they
 * already have their own folder). Membership is gated by the 24h window —
 * unlike a plain top-N, the folder can be empty and stays that way once
 * nothing has changed recently — then capped and displayed A→Z.
 */
export function selectRecentDashboards(
  dashboards: DashboardListItem[],
  cap = recentDashboardsCap,
  now = Date.now(),
): DashboardListItem[] {
  return dashboards
    .filter((d) => !d.isFavorite)
    .map((d) => ({
      d,
      at: Math.max(
        new Date(d.createdAt).getTime(),
        new Date(d.contentUpdatedAt ?? d.updatedAt).getTime(),
      ),
    }))
    .filter(({ at }) => now - at < dayMs)
    .sort((a, b) => b.at - a.at)
    .slice(0, cap)
    .map(({ d }) => d)
    .sort(byName);
}

/** The Favorites folder's members, A→Z. */
export function selectFavoriteDashboards(dashboards: DashboardListItem[]): DashboardListItem[] {
  return dashboards.filter((d) => d.isFavorite).sort(byName);
}

/**
 * Whether a dashboard carries the "shine" mark: its content (the dashboard
 * row OR any of its widgets — the server-derived `contentUpdatedAt`) changed
 * within the last day AND it hasn't been opened since that change. Opening it
 * acknowledges the change (the visit time outdates the update time), so the
 * mark only ever points at something the viewer hasn't seen yet.
 */
export function hasUnseenUpdate(
  d: DashboardListItem,
  visits: Record<string, number>,
  now = Date.now(),
): boolean {
  // Older servers don't send contentUpdatedAt — degrade to the row's own stamp.
  const updatedAt = new Date(d.contentUpdatedAt ?? d.updatedAt).getTime();
  return now - updatedAt < dayMs && updatedAt > (visits[d.id] ?? 0);
}
